import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart3, Download, Printer, Loader2, Inbox, CalendarDays, Users, Table2, FileDown, ListChecks, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import ReportFilters from '../components/reports/ReportFilters';
import ReportSummary from '../components/reports/ReportSummary';
import ReportRows from '../components/reports/ReportRows';
import ReportCollaborators from '../components/reports/ReportCollaborators';
import ReportDetailDialog from '../components/reports/ReportDetailDialog';
import WeeklyEmailPanel from '../components/reports/WeeklyEmailPanel';
import OrdersReport from '../components/reports/OrdersReport';
import { groupByDay, groupByCollaborator, quickRange, downloadCsv, printReport } from '../components/reports/reportUtils';
import { useLiveRefresh } from '../hooks/useLiveRefresh';
import { API } from '../utils/kitchenTasks';
import { todayZurich, formatDateFR } from '../utils/dateFormat';
import { formatApiError } from '../services/api';

const EMPTY = { rows: [], summary: { total: 0, demandees: 0, ouvertes: 0, terminees: 0, non_realisees: 0, annulees: 0, taux_realisation: null }, quantities_by_unit: [], by_collaborator: [], period: { from: '', to: '' } };
const VIEWS = [['day', 'Par jour et service', CalendarDays], ['collab', 'Par collaborateur', Users], ['recap', 'Récap collaborateurs', Table2]];

export default function ReportsPage() {
  const today = todayZurich();
  const [filters, setFilters] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    const ok = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v || '');
    const range = ok(q.get('from')) && ok(q.get('to')) ? { from: q.get('from'), to: q.get('to') } : quickRange('7d', today);
    return { ...range, service: '', category: '', assigned_user_id: '', state: '', source: '' };
  });
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('day');
  const [section, setSection] = useState(() => (new URLSearchParams(window.location.search).get('section') === 'commandes' ? 'commandes' : 'mise-en-place'));
  const [collaborators, setCollaborators] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => { axios.get(`${API}/users/kitchen-collaborators`).then(r => setCollaborators(r.data.data || [])).catch(() => {}); }, []);

  const fetchReport = useCallback(async (silent = false) => {
    if (!filters.from || !filters.to) return;
    if (!silent) setLoading(true);
    try {
      const params = { date_from: filters.from, date_to: filters.to };
      ['service', 'category', 'assigned_user_id', 'state', 'source'].forEach(k => { if (filters[k]) params[k] = filters[k]; });
      const res = await axios.get(`${API}/reports/mise-en-place`, { params });
      setData(res.data.data || EMPTY);
    } catch (err) { if (!silent) toast.error(formatApiError(err)); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchReport(); }, [fetchReport]);
  useLiveRefresh(() => fetchReport(true));

  const sections = useMemo(() => (view === 'collab' ? groupByCollaborator(data.rows) : groupByDay(data.rows)), [data.rows, view]);
  const invalidRange = filters.from && filters.to && filters.to < filters.from;

  const downloadPdf = async () => {
    try {
      const params = { date_from: filters.from, date_to: filters.to };
      ['service', 'category', 'assigned_user_id', 'state', 'source'].forEach(k => { if (filters[k]) params[k] = filters[k]; });
      const res = await axios.get(`${API}/reports/mise-en-place/export.pdf`, { params, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = `rapport-mise-en-place_${filters.from}_${filters.to}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen p-3 md:p-8 pb-24 md:pb-8" data-testid="reports-page">
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 tracking-tight flex items-center gap-2"><BarChart3 className="text-neon-pink" size={30} />Rapports</h1>
          <div className="inline-flex rounded-full border border-gray-200 bg-white p-1" data-testid="report-section-tabs">
            {[['mise-en-place', 'Mise en place', ListChecks], ['commandes', 'Commandes', ShoppingCart]].map(([k, l, Icon]) => (
              <button key={k} type="button" onClick={() => setSection(k)} data-testid={`report-section-${k}`} className={`h-10 px-4 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${section === k ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}><Icon size={14} />{l}</button>
            ))}
          </div>
        </div>

        {section === 'commandes' ? <OrdersReport today={today} /> : (<>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <h2 className="font-heading text-lg font-bold text-gray-900">Mise en place</h2>
            <p className="text-gray-500 text-sm" data-testid="report-period-label">Du {formatDateFR(filters.from)} au {formatDateFR(filters.to)} · {data.rows.length} ligne{data.rows.length > 1 ? 's' : ''}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => downloadCsv(data.rows, data.period)} disabled={loading || data.rows.length === 0} className="h-11 rounded-full border-gray-300 text-gray-700" data-testid="report-export-csv-btn"><Download size={16} className="mr-1.5" />Export CSV</Button>
            <Button variant="outline" onClick={downloadPdf} disabled={loading || data.rows.length === 0} className="h-11 rounded-full border-gray-300 text-gray-700" data-testid="report-download-pdf-btn"><FileDown size={16} className="mr-1.5" />Télécharger PDF</Button>
            <Button onClick={() => printReport(data, sections.length ? sections : groupByDay(data.rows))} disabled={loading || data.rows.length === 0} className="h-11 rounded-full bg-gray-900 hover:bg-gray-800 text-white" data-testid="report-print-btn"><Printer size={16} className="mr-1.5" />Imprimer</Button>
          </div>
        </div>

        <WeeklyEmailPanel period={{ from: filters.from, to: filters.to }} />

        <ReportFilters filters={filters} onChange={setFilters} collaborators={collaborators} today={today} onRefresh={() => fetchReport()} loading={loading} />
        {invalidRange && <p className="text-sm text-red-600" data-testid="report-range-error">La date de fin doit être postérieure à la date de début.</p>}

        <ReportSummary summary={data.summary} units={data.quantities_by_unit} />

        <div className="flex flex-wrap gap-2" data-testid="report-view-tabs">
          {VIEWS.map(([k, l, Icon]) => (
            <button key={k} type="button" onClick={() => setView(k)} data-testid={`report-view-${k}`}
              className={`h-10 px-4 rounded-full text-sm font-semibold border inline-flex items-center gap-1.5 transition-colors ${view === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}><Icon size={14} />{l}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-10 h-10 text-neon-pink animate-spin" /></div>
        ) : data.rows.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-10 text-center" data-testid="report-empty">
            <Inbox className="mx-auto text-gray-300 mb-2" size={36} />
            <p className="text-gray-600 font-medium">Aucune mise en place pour cette période et ces filtres.</p>
          </div>
        ) : view === 'recap' ? (
          <ReportCollaborators rows={data.by_collaborator} onSelect={(c) => { if (c.id !== '__none' && collaborators.some(x => x.id === c.id)) { setFilters(f => ({ ...f, assigned_user_id: c.id })); setView('collab'); } }} />
        ) : (
          <ReportRows sections={sections} onOpen={setSelected} />
        )}
        </>)}
      </div>
      <ReportDetailDialog row={selected} open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }} />
    </div>
  );
}
